import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { updateProductos, createCategoria } from '../../services';
import '../../adminStyles.css';

const ActProductos = ({ categorias, onProductUpdate, productoParaActualizar, onClose }) => {
    const [productoSel, setProductoSel] = useState('');
    const [crearNuevaCategoria, setCrearNuevaCategoria] = useState(false);
    const [nuevaCategoria, setNuevaCategoria] = useState('');
    const [feedback, setFeedback] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imagenPreview, setImagenPreview] = useState(null);

    const [datosProducto, setDatosProducto] = useState({
        nombre: '', descripcion: '', categoria: '',
        precio: '', imagen: '', tipoWhisky: '', destacado: false,
    });

    const inputFileRef = useRef(null);

    useEffect(() => {
        if (productoParaActualizar) {
            setProductoSel(productoParaActualizar._id);
            setDatosProducto({
                nombre: productoParaActualizar.nombre,
                descripcion: productoParaActualizar.descripcion,
                categoria: productoParaActualizar.categoria?._id || '',
                precio: productoParaActualizar.precio,
                imagen: productoParaActualizar.imagen || '',
                tipoWhisky: productoParaActualizar.tipoWhisky || '',
                destacado: productoParaActualizar.destacado || false,
            });
            setImagenPreview(null);
        }
    }, [productoParaActualizar]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDatosProducto((prev) => ({ ...prev, [name]: value }));
    };

    const handleCategoriaChange = (e) => {
        const v = e.target.value;
        if (v === 'crear') {
            setCrearNuevaCategoria(true);
            setDatosProducto((prev) => ({ ...prev, categoria: '' }));
        } else {
            setCrearNuevaCategoria(false);
            setDatosProducto((prev) => ({ ...prev, categoria: v }));
        }
    };

    const validarImagen = (file) =>
        ['image/jpeg', 'image/jpg', 'image/png'].includes(file?.type);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!validarImagen(file)) {
            setFeedback({ type: 'error', msg: 'Solo se permiten imágenes JPG, JPEG o PNG.' });
            e.target.value = '';
            return;
        }
        setImagenPreview(URL.createObjectURL(file));
        setDatosProducto((prev) => ({ ...prev, imagen: '' }));
        setFeedback(null);
    };

    const handleSubmit = async () => {
        if (!productoSel) return;

        let categoriaFinal = datosProducto.categoria;
        if (crearNuevaCategoria && nuevaCategoria.trim()) {
            try {
                const res = await createCategoria(nuevaCategoria);
                categoriaFinal = res.data.categoria._id;
            } catch {
                setFeedback({ type: 'error', msg: 'Error al crear nueva categoría.' });
                return;
            }
        }

        const formData = new FormData();
        formData.append('nombre', datosProducto.nombre);
        formData.append('descripcion', datosProducto.descripcion);
        formData.append('categoria', categoriaFinal);
        formData.append('precio', datosProducto.precio);
        formData.append('tipoWhisky', datosProducto.tipoWhisky);
        formData.append('destacado', datosProducto.destacado ? 'true' : 'false');

        const imagen = inputFileRef.current?.files[0];
        if (imagen) {
            if (!validarImagen(imagen)) {
                setFeedback({ type: 'error', msg: 'Solo se permiten imágenes JPG, JPEG o PNG.' });
                return;
            }
            formData.append('imagen', imagen);
        }

        setLoading(true);
        try {
            await updateProductos(productoSel, formData);
            await onProductUpdate();
            setFeedback({ type: 'success', msg: 'Producto actualizado con éxito.' });
            setTimeout(() => onClose(), 1200);
        } catch (error) {
            console.error('Error al actualizar producto:', error);
            setFeedback({ type: 'error', msg: 'Error al actualizar el producto. Intentá de nuevo.' });
        } finally {
            setLoading(false);
        }
    };

    const imagenActual = imagenPreview || datosProducto.imagen;

    return (
        <div className="admin-form-modal">
            <div className="admin-form-header">
                <div>
                    <span className="admin-form-eyebrow">Catálogo</span>
                    <h2 className="admin-form-title">Editar Whisky</h2>
                </div>
                <button className="admin-modal-close" onClick={onClose} aria-label="Cerrar"><FontAwesomeIcon icon={faXmark} /></button>
            </div>

            {feedback && (
                <div className={`admin-form-feedback admin-form-feedback--${feedback.type}`}>
                    {feedback.msg}
                </div>
            )}

            <div className="admin-form-body">
                <div>
                    <div className="admin-img-upload" onClick={() => inputFileRef.current?.click()}>
                        <input
                            type="file"
                            ref={inputFileRef}
                            accept="image/jpeg,image/jpg,image/png"
                            style={{ display: 'none' }}
                            onChange={handleImageChange}
                        />
                        {imagenActual ? (
                            <img src={imagenActual} alt="preview" className="admin-img-preview" />
                        ) : (
                            <div className="admin-img-upload-placeholder">
                                <FontAwesomeIcon icon={faCloudArrowUp} className="admin-img-upload-icon" />
                                <span>Cambiar imagen</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="admin-form-fields">
                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label className="admin-form-label">Nombre / Marca</label>
                            <input
                                className="admin-form-input"
                                name="nombre"
                                value={datosProducto.nombre}
                                onChange={handleChange}
                                placeholder="Nombre | Marca"
                            />
                        </div>
                        <div className="admin-form-group">
                            <label className="admin-form-label">Precio</label>
                            <input
                                className="admin-form-input"
                                type="number"
                                name="precio"
                                value={datosProducto.precio}
                                onChange={handleChange}
                                placeholder="$"
                            />
                        </div>
                    </div>

                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label className="admin-form-label">Categoría</label>
                            <select
                                className="admin-form-select"
                                name="categoria"
                                value={datosProducto.categoria}
                                onChange={handleCategoriaChange}
                            >
                                <option value="">Seleccionar categoría</option>
                                {categorias.map((cat) => (
                                    <option key={cat._id} value={cat._id}>{cat.nombre}</option>
                                ))}
                                <option value="crear">+ Crear nueva categoría</option>
                            </select>
                            {crearNuevaCategoria && (
                                <input
                                    className="admin-form-input"
                                    style={{ marginTop: '0.4rem' }}
                                    placeholder="Nueva categoría"
                                    value={nuevaCategoria}
                                    onChange={(e) => setNuevaCategoria(e.target.value)}
                                />
                            )}
                        </div>
                        <div className="admin-form-group">
                            <label className="admin-form-label">Tipo de Whisky</label>
                            <select
                                className="admin-form-select"
                                name="tipoWhisky"
                                value={datosProducto.tipoWhisky}
                                onChange={handleChange}
                            >
                                <option value="">Seleccionar tipo</option>
                                <option value="Blend">Blend</option>
                                <option value="Blend Scotch">Blend Scotch</option>
                                <option value="Bourbon">Bourbon</option>
                                <option value="Irish Malted">Irish</option>
                                <option value="Single Malt">Single Malt</option>
                            </select>
                        </div>
                    </div>

                    <div className="admin-form-group">
                        <label className="admin-form-label">Descripción</label>
                        <textarea
                            className="admin-form-textarea"
                            name="descripcion"
                            value={datosProducto.descripcion}
                            onChange={handleChange}
                            placeholder="Descripción del producto..."
                        />
                    </div>

                    <label className="admin-form-check">
                        <input
                            type="checkbox"
                            checked={datosProducto.destacado}
                            onChange={(e) =>
                                setDatosProducto((prev) => ({ ...prev, destacado: e.target.checked }))
                            }
                        />
                        <span className="admin-form-check-label">Producto destacado</span>
                    </label>
                </div>
            </div>

            <div className="admin-form-footer">
                <button className="admin-btn-danger-ghost" onClick={onClose}>Cancelar</button>
                <button className="admin-btn-primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Guardando...' : 'Guardar cambios'}
                </button>
            </div>
        </div>
    );
};

export default ActProductos;
